/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { IClientAuthDTO } from "@hooks/dtos/IClientDTO";
import {
  clearPlatformAffiliateReferral,
  getPlatformAffiliateReferral,
} from "@hooks/functions/Referral/storage";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { ZuLoadingStore } from "@hooks/stores/zustand-loading-store";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { config } from "config";
import { ReactNode, createContext, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export interface IFormPasswordLogin {
  password: string;
}

interface ISignInProps {
  userName: string;
  password: string;
}

interface IIdentifyProps {
  document: string;
}

interface IRegisterProps {
  gender?: number;
  userName: string;
  name: string;
  password: string;
  passwordConfirm: string;
  email: string;
  emailConfirm: string;
  cellPhone: string;
  termsCheck: string;
  birth?: string;
  language: string;
  document?: string;
  referral?: number;
}

export interface ICompleteClientProfileProps {
  name: string;
  email: string;
  cellPhone: string;
  password: string;
  passwordConfirm: string;
}

export interface IUpdateClientProfileProps {
  name?: string;
  email?: string;
  cellPhone?: string;
  currentPassword?: string;
  password?: string;
}

export interface IUpdateClientPasswordProps {
  currentPassword: string;
  password: string;
  passwordConfirm: string;
}

interface AuthContextData {
  identify(data: IIdentifyProps): Promise<void>;
  signIn(data: ISignInProps): Promise<void>;
  passwordSignIn(data: IFormPasswordLogin): Promise<void>;
  signOut: () => void;
  createClient(data: IRegisterProps): Promise<void>;
  refreshClient(): Promise<void>;
  completeClientProfile(data: ICompleteClientProfileProps): Promise<void>;
  updateClientProfile(data: IUpdateClientProfileProps): Promise<void>;
  updateClientPassword(data: IUpdateClientPasswordProps): Promise<void>;
  checkAtSignAvailability(atSign: string): Promise<boolean>;
  updateClientAtSign(atSign: string): Promise<void>;
  client: IClientAuthDTO | undefined;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const {
    client,
    actions: { setClient, setSessionId },
  } = ZuAuthStore();

  const {
    actions: { toggleLockLoading },
  } = ZuLoadingStore();

  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  function clearHash() {
    if (window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  async function openSession() {
    toggleLockLoading(true);
    try {
      await api.get("/client/session/open");
    } catch (error) {
      // console.log('error auth: ', error)
    }
    toggleLockLoading(false);
  }

  async function getSessionInfo() {
    try {
      const response = await api.get("/client/session/info");
      return response;
    } catch (err) {
      //
    }
  }

  async function identify(data: IIdentifyProps) {
    await openSession();

    const affiliate = getPlatformAffiliateReferral();
    const sessionId = localStorage.getItem(`@${config.appAgent}:token-id`);
    try {
      const dataSend = {
        document: data.document,
        affiliate,
        platform: "s",
        token: sessionId,
        csrfInfo: sessionId,
      };
      const response = await api.post("/client/session/identify", dataSend);

      const { stage, client } = response.data;

      setClient({ client, stage });
      clearPlatformAffiliateReferral();

      pushToastify({
        message: "Login realizado com sucesso!",
        type: "success",
        theme: "dark",
      });
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Ocorreu um erro desconhecido",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function signIn(data: ISignInProps) {
    await openSession();

    const sessionId = localStorage.getItem(`@${config.appAgent}:token-id`);
    try {
      const response = await api.post("/client/session/sign-in", {
        userName: data.userName,
        password: data.password,
        platform: "s",
        token: sessionId,
        csrfInfo: sessionId,
      });

      const { client, stage } = response.data;

      setClient({ client, stage });
      clearPlatformAffiliateReferral();

      pushToastify({
        message: "Login realizado com sucesso!",
        type: "success",
        theme: "dark",
      });

      clearHash();

      navigate("/dashboard");
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Ocorreu um erro desconhecido",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function passwordSignIn(data: IFormPasswordLogin) {
    const sessionId = localStorage.getItem(`@${config.appAgent}:token-id`);
    try {
      const response = await api.post("/client/session/password/sign-in", {
        password: data.password,
        platform: "s",
        token: sessionId,
        csrfInfo: sessionId,
      });

      const { client, stage } = response.data;

      setClient({ client, stage });
      clearPlatformAffiliateReferral();

      pushToastify({
        message: "Autenticação realizada com sucesso!",
        type: "success",
        theme: "dark",
      });

      clearHash();
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Ocorreu um erro desconhecido",
        type: "error",
        theme: "dark",
      });
      throw error;
    }
  }

  async function createClient(data: IRegisterProps) {
    try {
      const affiliate = getPlatformAffiliateReferral();
      const dataSend = {
        customer: {
          gender: data.gender,
          userName: data.userName,
          name: data.name,
          password: data.password,
          passwordConfirm: data.passwordConfirm,
          email: data.email,
          emailConfirm: data.email,
          cellPhone: data.cellPhone,
          termsCheck: data.termsCheck ? "y" : "n",
          birth: data.birth,
          language: "pt-br",
          referral: data.referral,
          document: data.document,
          documentType: 1,
        },
        affiliate,
      };
      console.log({ dataSend });
      await api.post("/client/create", dataSend);

      // Logando o usuário após o seu cadastro
      clearPlatformAffiliateReferral();
      signIn({ userName: data.email, password: data.password });
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data?.message,
        type: "error",
        theme: "dark",
      });
    }
  }

  async function getUserProfile() {
    try {
      const response = await api.get("/client/info");
      const clientData = {
        ...(client?.client || {}),
        ...(response.data.client || {}),
      };

      setClient({
        client: {
          ...clientData,
        },
        stage: response.data.stage || response.data.clientInfo || client?.stage,
      });
    } catch (error: any) {
      // console.error(
      //   'Erro ao resgatar informações do perfil do usuário: ',
      //   error,
      // )
    }
  }

  async function refreshClient() {
    await getUserProfile();
  }

  async function syncClientFromResponse(responseData: any) {
    if (responseData?.client) {
      setClient({
        client: responseData.client,
        stage: responseData.stage || client?.stage || "identified",
      });
      return;
    }

    await refreshClient();
  }

  async function completeClientProfile(data: ICompleteClientProfileProps) {
    try {
      const response = await api.put("/client/profile/complete", data);
      await syncClientFromResponse(response.data);

      pushToastify({
        message: "Cadastro atualizado com sucesso!",
        type: "success",
        theme: "dark",
      });
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Ocorreu um erro ao atualizar",
        type: "error",
        theme: "dark",
      });
      throw error;
    }
  }

  async function updateClientProfile(data: IUpdateClientProfileProps) {
    try {
      const response = await api.put("/client/profile", data);
      await syncClientFromResponse(response.data);

      pushToastify({
        message: "Perfil atualizado com sucesso!",
        type: "success",
        theme: "dark",
      });
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Ocorreu um erro ao atualizar",
        type: "error",
        theme: "dark",
      });
      throw error;
    }
  }

  async function updateClientPassword(data: IUpdateClientPasswordProps) {
    try {
      await api.put("/client/password", data);

      pushToastify({
        message: "Senha atualizada com sucesso!",
        type: "success",
        theme: "dark",
      });
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Ocorreu um erro ao alterar a senha",
        type: "error",
        theme: "dark",
      });
      throw error;
    }
  }

  function normalizeAtSign(atSign: string) {
    return atSign.trim().replace(/^@+/, "").toLowerCase();
  }

  async function checkAtSignAvailability(atSign: string) {
    try {
      const cleanAtSign = normalizeAtSign(atSign);
      const response = await api.get("/client/profile/at-sign/availability", {
        params: { atSign: cleanAtSign },
      });

      return Boolean(response.data.available);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Não foi possível consultar o @usuario",
        type: "error",
        theme: "dark",
      });
      throw error;
    }
  }

  async function updateClientAtSign(atSign: string) {
    try {
      const cleanAtSign = normalizeAtSign(atSign);
      const response = await api.put("/client/profile/at-sign", {
        atSign: cleanAtSign,
      });
      await syncClientFromResponse(response.data);

      pushToastify({
        message: "@usuario atualizado com sucesso!",
        type: "success",
        theme: "dark",
      });
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message || "Ocorreu um erro ao salvar o @",
        type: "error",
        theme: "dark",
      });
      throw error;
    }
  }

  async function signOut() {
    setClient(undefined);
    localStorage.removeItem(`@${config.appAgent}:client`);
    openSession();

    // clearHash();
  }

  async function checkSavedSession() {
    toggleLockLoading(true);
    try {
      const data = localStorage.getItem(`@${config.appAgent}:client`);
      const sessionId = localStorage.getItem(`@${config.appAgent}:token-id`);
      if (!sessionId) {
        await openSession();
      } else {
        await getSessionInfo();
      }
      if (data && sessionId) {
        setClient(JSON.parse(data));
      }
    } catch {
      await openSession();
    }
    toggleLockLoading(false);
  }

  useEffect(() => {
    const responseInterceptor = api.interceptors.response.use(
      async (response) => {
        if (response.headers["token-id"]) {
          setSessionId(response.headers["token-id"]);
        }
        return response;
      },
      async (error) => {
        const config = error.config;
        const errorCode = String(error?.response?.data?.errorCode || "");
        if (errorCode === "CSRFX004" || errorCode === "CSRFX005") {
          if (config && config.retryCount < 1) {
            config.retryCount += 1;
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const session = await getSessionInfo();

            config.headers["token-id"] = session?.data.id;
            config.headers["token-csrf"] = session?.data.csrf;

            if (session?.data.id) {
              localStorage.setItem(
                `@${config.appAgent}:token-id`,
                String(session?.data.id),
              );
            }
            if (session?.data.csrf) {
              localStorage.setItem(
                `@${config.appAgent}:token-csrf`,
                String(session?.data.csrf),
              );
            }

            return api(config);
          }
        } else if (errorCode.match("CSRFX")) {
          signOut();
          await openSession();
        }
        return Promise.reject(error);
      },
    );

    return () => {
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    checkSavedSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        identify,
        signIn,
        passwordSignIn,
        signOut,
        createClient,
        refreshClient,
        completeClientProfile,
        updateClientProfile,
        updateClientPassword,
        checkAtSignAvailability,
        updateClientAtSign,
        client,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);
